import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AutenticacionService } from './autenticacion.service';

export interface ColoresConfig {
  colorPrimario: string;
  colorSecundario: string;
}

@Injectable({
  providedIn: 'root'
})
export class ColoresService {
  private apiUrl = 'public/configuracion/1';
  private updateUrl = 'admin/actualizar_colores';

  constructor(
    private http: HttpClient,
    private auth: AutenticacionService
  ) {}

  /**
   * Obtiene los colores desde la configuración
   */
  getColores(): Promise<ColoresConfig> {
    return this.auth.get(this.apiUrl).then((response: any) => {
      if (response.status && response.response) {
        return {
          colorPrimario: response.response.colorPrimario || '',
          colorSecundario: response.response.colorSecundario || ''
        };
      }
      return { colorPrimario: '', colorSecundario: '' };
    }).catch((error: any) => {
      console.error('Error al obtener colores:', error);
      return { colorPrimario: '', colorSecundario: '' };
    });
  }

  /**
   * Carga y aplica los colores desde la API
   * Este método está diseñado para ser usado en APP_INITIALIZER
   */
  loadAndApplyColores(): Promise<void> {
    // Verificar que estamos en el navegador (no en SSR)
    if (typeof document === 'undefined') {
      return Promise.resolve();
    }

    return this.getColores().then((colores) => {
      if (colores.colorPrimario || colores.colorSecundario) {
        this.applyColoresToDocument(colores);
      }
      return Promise.resolve();
    }).catch((error) => {
      console.error('Error al cargar colores en APP_INITIALIZER:', error);
      // No rechazar la promesa para que la app pueda inicializarse incluso si falla
      return Promise.resolve();
    });
  }

  /**
   * Actualiza los colores en el servidor
   */
  updateColores(colorPrimario: string, colorSecundario: string, token: string): Promise<any> {
    const formData = new FormData();
    
    if (colorPrimario) {
      formData.append('color_primario', colorPrimario);
    }
    if (colorSecundario) {
      formData.append('color_secundario', colorSecundario);
    }

    const headers = new HttpHeaders({
      'X-API-TOKEN': token
    });

    return this.http.post(environment.APP_API_BASE + this.updateUrl, formData, { headers }).toPromise();
  }

  /**
   * Valida el formato de un color hexadecimal
   */
  validateColor(color: string): boolean {
    if (!color) return true; // Los colores son opcionales
    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
    return hexColorRegex.test(color);
  }

  /**
   * Aplica los colores como variables CSS en el root del documento
   */
  applyColoresToDocument(colores: ColoresConfig) {
    if (typeof document === 'undefined') {
      return; // No ejecutar en servidor (SSR)
    }
    
    const root = document.documentElement;
    
    if (colores.colorPrimario && colores.colorPrimario.trim() !== '') {
      root.style.setProperty('--color-primario', colores.colorPrimario);
      console.log('Color primario aplicado:', colores.colorPrimario);
    } else {
      // Si no hay color, mantener el valor por defecto (no remover)
      console.log('Color primario vacío, usando valor por defecto');
    }
    
    if (colores.colorSecundario && colores.colorSecundario.trim() !== '') {
      root.style.setProperty('--color-secundario', colores.colorSecundario);
      console.log('Color secundario aplicado:', colores.colorSecundario);
    } else {
      // Si no hay color, mantener el valor por defecto (no remover)
      console.log('Color secundario vacío, usando valor por defecto');
    }
  }
}

